import "../index.css";
import React, { useState, useEffect } from "react";
import { getTimings } from "../server";
import { checkVersion } from "../version";
import Timer from "./Timer";
import Iframe from "./Iframe";
import PrayerTimesTable from "./PrayerTimesTable";
import LocationInput from "./LocationInput";

function Home() {
  const [prayerState, setPrayerState] = useState({
    allPrayerData: null,
    iftarData: null,
    sehriData: null
  });
  const [loading, setLoading] = useState("Please enable location for Iftar Timer to fetch accurate timings.");
  const [locationFailed, setLocationFailed] = useState(false);

  useEffect(() => {
    // Check geolocation permission status
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "geolocation" }).then(result => {
        setLoading(result.state === "granted"
          ? "Fetching prayer timings, please wait..."
          : "Please enable location for Iftar Timer to fetch accurate timings."
        );
      }).catch(err => {
        console.log("Permission query not supported:", err);
      });
    }
  }, []);

  const processTimings = (timings) => {
    try {
      // Validate timings structure
      if (!timings?.prayerTimesToday?.timings || !timings?.prayerTimesTomorrow?.timings) {
        throw new Error("Invalid timings data structure");
      }

      const today = new Date();
      const sunsetTime = getTimeFromString(timings.prayerTimesToday.timings.Sunset, today);
      const currentTime = today.getTime();
      const isAfterSunset = currentTime > sunsetTime && currentTime < new Date(today.setHours(23, 59, 59)).getTime();

      return {
        allPrayerData: timings,
        iftarData: {
          date: formatDate(timings.prayerTimesToday.date),
          time: timings.prayerTimesToday.timings.Sunset
        },
        sehriData: {
          date: formatDate(isAfterSunset
            ? timings.prayerTimesTomorrow.date
            : timings.prayerTimesToday.date),
          time: isAfterSunset
            ? timings.prayerTimesTomorrow.timings.Fajr
            : timings.prayerTimesToday.timings.Fajr
        }
      };
    } catch (error) {
      console.error("Error processing timings:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchTimings = async () => {
      try {
        // Check app version and clear old cache if needed
        checkVersion();

        const cachedTimings = localStorage.getItem('prayerTimings');
        const lastFetchDate = localStorage.getItem('lastFetchDate');
        const today = new Date();
        const currentDate = today.toDateString();

        if (cachedTimings && lastFetchDate === currentDate) {
          try {
            const parsedTimings = JSON.parse(cachedTimings);
            const processedState = processTimings(parsedTimings);

            if (processedState) {
              setPrayerState(processedState);
              return;
            } else {
              // Invalid cached data, clear and refetch
              console.warn("Invalid cached data, clearing cache");
              localStorage.removeItem('prayerTimings');
              localStorage.removeItem('lastFetchDate');
            }
          } catch (parseError) {
            console.error("Error parsing cached timings:", parseError);
            localStorage.removeItem('prayerTimings');
            localStorage.removeItem('lastFetchDate');
          }
        }

        const timings = await getTimings();

        // Check if location detection failed
        if (!timings) {
          console.error('Failed to get location and timings');
          setLocationFailed(true);
          setLoading("Location detection failed. Please enter your location manually.");
          return;
        }

        localStorage.setItem('prayerTimings', JSON.stringify(timings));
        localStorage.setItem('lastFetchDate', currentDate);
        setPrayerState(processTimings(timings));
      } catch (error) {
        console.error('Error fetching prayer times:', error);
        setLocationFailed(true);
        setLoading("An error occurred. Please enter your location manually.");
      }
    };

    fetchTimings();
  }, []);

  const { allPrayerData, iftarData, sehriData } = prayerState;
  const isLoading = !allPrayerData || !iftarData || !sehriData;

  const handleReset = () => {
    ["lastFetchDate", "prayerTimings", "cachedLocation"].forEach((key) => {
      localStorage.removeItem(key);
    });
    window.location.reload();
  };

  const handleLocationSet = () => {
    // After location is set manually, reload to fetch timings
    window.location.reload();
  };

  return (
    <div className="App">
      {locationFailed ? (
        <LocationInput onLocationSet={handleLocationSet} />
      ) : isLoading ? (
        <div className="loading">
          <Iframe title="loading" src="https://giphy.com/embed/n1KAZ8ydmwlskkvQEJ" />
          <h2>{loading}</h2>
          <h1 onClick={handleReset} style={{ cursor: "pointer" }}>Refresh</h1>
        </div>
      ) : (
        <>
          <Timer
            iftarData={iftarData}
            sehriData={sehriData}
            currentLocation={allPrayerData.location.currentLocation}
          />
          <PrayerTimesTable prayerTimes={allPrayerData.prayerTimesToday} />
        </>
      )}
      <div className="footer">
        <h2>
          Made by{" "}
          <a href="https://github.com/tahsinocity/" target="_blank" rel="noopener noreferrer">
            @tahsinocity
          </a>
        </h2>
      </div>
    </div>
  );
}

const formatDate = (date) => date ? date.replace(/-/g, "/") : "";

const getTimeFromString = (timeString, date) => {
  try {
    if (!timeString || !timeString.includes(':')) {
      console.error("Invalid time string:", timeString);
      return new Date().getTime(); // Return current time as fallback
    }
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (isNaN(h) || isNaN(m)) {
      console.error("Invalid time components:", hours, minutes);
      return new Date().getTime();
    }

    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m).getTime();
  } catch (error) {
    console.error("Error parsing time:", error);
    return new Date().getTime();
  }
};

export default Home;
